/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Constants, copy, getUpdatedState } from '../utils';
import { SchoolService, SettingsService, TeacherGroupService } from '../services';

export const useIndicators = () => {
  const initialForm = {
    school_year_id: '',
    school_year_name: '',
    school_grade_id: '',
    subject_id: '',
    subject_name: '',
  };

  const emptyItem = {
    id: '',
    description: '',
    // Valores para el placeholder
    _description: ' ',
  };

  const user = useSelector(state => state.user);
  const [form, setForm] = useState(initialForm);
  const [schoolYears, setSchoolYears] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Establece el servicio de acuerdo a la rol del usuario
  const service = useMemo(() => {
    switch (user.level_id) {
      case Constants.USER.LEVELS.SCHOOL: return SettingsService.school;
      case Constants.USER.LEVELS.PROFESSOR: return SettingsService.teacher;
      default: return null;
    }
  }, [user.id]);

  const fetchData = async () => {
    setIsLoadingData(true);

    try {
      const filters = {
        school_id: user.id,
        school_year_id: form.school_year_id,
        perPage: 200,
      };
      const response = await service?.getIndicators(filters);
      setData(response);
      fillIndicators();

    } catch (error) {
      console.log(error)
    }

    setIsLoadingData(false);
  }

  const fetchSchoolYears = async () => {
    setIsLoading(true);

    SchoolService.schoolAdmin.getSchoolarsYears({ school_id: user.id })
      .then(res => {
        setSchoolYears(res.map(item => ({ value: String(item.id), label: item.name })));
      })
      .catch(console.log)
      .finally(() => setIsLoading(false));
  }

  const fetchSelects = async () => {
    setIsLoading(true);

    if (user.level_id === Constants.USER.LEVELS.SCHOOL) {
      const filter = {
        school_year_id: form.school_year_id,
        school_id: user.id,
      };

      Promise.all([
        SchoolService.schoolAdmin.getGrades(filter),
        SchoolService.schoolAdmin.getSubjects(filter),
      ])
      .then(res => {
        setGrades(res[0].map(item => ({ ...item, value: String(item.id), label: item.name })));
        setSubjects(res[1].data);
      })
      .catch(console.log)
      .finally(() => setIsLoading(false));

    } else {
      // Obtiene todos los grados y sus materias
      const filter = {
        school_year_id: form.school_year_id,
        professor_id: user.id,
        omit_sections: true,
        omit_subjects: true,
      };

      TeacherGroupService.groups.getSubjects(filter)
        .then(res => {
          setGrades(res.data.map(item => ({ ...item, value: String(item.id), label: item.name })));
          setSubjects(res.data);
        })
        .catch(console.log)
        .finally(() => setIsLoading(false));
    }
  }

  const add = () => {
    setIndicators(indicators.concat([emptyItem]));
  }

  const remove = (index) => {
    setIndicators(indicators.filter((_, i) => i !== index));
  }

  const removeFromBD = async (item) => {
    setIsLoading(true);

    try {
      await service?.removeIndicator(item);
      fetchData();
      toast.success('Indicador eliminado correctamente');

    } catch (error) {
      console.log(error);
    }

    setIsLoading(false);
  }

  const onChange = (value, target) => {
    setForm(s => {
      const newState = copy(s);
      newState[target] = value;

      if (target === 'subject_id') {
        const subject = filteredSubjects?.find(item => item.value === value);
        newState.subject_name = subject.name ?? '';
      }

      return newState;
    });

    if (target === 'subject_id') fillIndicators();

    if (target === 'school_grade_id') {
      const grade = subjects.find(item => item.id === Number(value));
      const _subjects = grade?.subjects ?? [];
      setFilteredSubjects(_subjects.map(item => ({ ...item, value: String(item.id), label: item.code })));
    }
  }

  const fillIndicators = async () => {
    let _form = await getUpdatedState(setForm);
    let _data = await getUpdatedState(setData);

    const _indicators = _data?.indicators
      ?.filter(i => i.school_grade_id === Number(_form.school_grade_id) && i.subject_id === Number(_form.subject_id))
      ?.map(i => ({ ...i, _description: i.description }));

    setIndicators(_indicators);
  }

  const onChangeIndicator = (value, target, index) => {
    setIndicators(s => {
      const data = copy(s);
      data[index][target] = value;
      return data;
    });
  }

  const resetForm = () => {
    const schoolYear = schoolYears.find(item => item.value === form.school_year_id);
    setForm(s => ({
      ...s,
      ...initialForm,
      // Mantiene el año escolar seleccionado para buscar los datos en base a este
      school_year_id: s.school_year_id,
      school_year_name: schoolYear?.label ?? '',
    }));
    setIndicators([]);
  }

  const isValidForm = (showError = true) => {
    const onError = (msg) => {
      if (showError) toast.error(msg);
      return false;
    }

    if (indicators.length === 0) {
      return onError('Debe agregar al menos un indicador');
    }

    if (indicators.some(item => item.description === '')) {
      return onError('Debe indicar el nombre de todos los indicadores');
    }

    return true;
  }

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);

    try {
      const formData = {
        school_id: user.id,
        school_year_id: form.school_year_id,
        school_grade_id: form.school_grade_id,
        subject_id: form.subject_id,
        indicators: indicators.map(item => ({
          id: item.id ?? undefined,
          description: item.description,
        })),
      };
      await service?.saveIndicators(formData);
      fetchData();
      toast.success('Datos actualizados correctamente');

    } catch (error) {
      console.log(error)
    }

    setIsLoading(false);
  }

  useEffect(() => {
    fetchSchoolYears();
  }, []);

  useEffect(() => {
    if (form.school_year_id) {
      fetchData();
      fetchSelects();
      resetForm();
    }
  }, [form.school_year_id]);

  return {
    add,
    data,
    form,
    grades,
    indicators,
    isLoading,
    isLoadingData,
    onChange,
    onChangeIndicator,
    remove,
    removeFromBD,
    schoolYears,
    subjects: filteredSubjects,
    submit,
  }
}
